import { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Mail, MessageSquare, Clock, Percent, Save, Building2, Globe, Plus, RefreshCw, Trash2, CheckCircle2, XCircle, Shield, Link2, Eye, EyeOff, Copy } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { Slider } from '../../../../ui/slider';
import { Badge } from '../../../../ui/badge';
import { Switch } from '../../../..//ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../../ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../ui/tabs';
import { epicengageSupabase } from '../../../../../integrations/supabase/client'
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { ToneType, IndustryType, RegionType } from '../../../../../types/epicengage';

import {  apiUrl, clientheaderconfig  } from '../../../../Helpers/Config';
import axios from "axios";

interface SenderIdentity {
  id: string;
  type: 'email' | 'domain';
  value: string;
  status: 'pending' | 'verified' | 'failed';
  requested_at: string;
  verified_at: string | null;
  updated_at: string;
  dns_records: any;
}

const INDUSTRIES: { value: IndustryType; label: string }[] = [
  { value: 'FNB', label: 'Food & Beverage' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'WELLNESS', label: 'Wellness & Spa' }
];

const REGIONS: { value: RegionType; label: string }[] = [
  { value: 'SG', label: 'Singapore' },
  { value: 'US', label: 'United States' },
  { value: 'GLOBAL', label: 'Global / Other' }
];

export default function Settings() {
  const { merchant, settings, updateSettings, updateMerchant } = useMerchant();
  const [saving, setSaving] = useState(false);
  
  // Sender identities
  const [identities, setIdentities] = useState<SenderIdentity[]>([]);
  const [loadingIdentities, setLoadingIdentities] = useState(true);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [addDomainOpen, setAddDomainOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<SenderIdentity | null>(null);
  
  // Business profile form
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState<IndustryType>('RETAIL');
  const [region, setRegion] = useState<RegionType>('GLOBAL');
  
  // Settings form
  const [formData, setFormData] = useState({
    default_sender_email: '',
    tone: 'FRIENDLY' as ToneType,
    weekly_send_cap: 2,
    quiet_hours_start: 22,
    quiet_hours_end: 8,
    discount_ceiling_percent: 20
  });

  // Webhook settings
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);

  useEffect(() => {
    if (merchant) {
      setBusinessName(merchant.name || '');
      setIndustry(merchant.industry || 'RETAIL');
      setRegion(merchant.region || 'GLOBAL');
    }
  }, [merchant]);

  useEffect(() => {
    if (settings) {
      setFormData({
        default_sender_email: settings.default_sender_email || '',
        tone: settings.tone,
        weekly_send_cap: settings.weekly_send_cap,
        quiet_hours_start: settings.quiet_hours_start,
        quiet_hours_end: settings.quiet_hours_end,
        discount_ceiling_percent: settings.discount_ceiling_percent
      });
    }
  }, [settings]);

  useEffect(() => {
    loadIdentities();
    loadWebhookSettings();
  }, [merchant]);

  const loadIdentities = async () => {
    try {
      const { data, error } = await epicengageSupabase.functions.invoke('email-identity-list');
      if (error) throw error;
      setIdentities(data.identities || []);
    } catch (error: any) {
      console.error('Failed to load identities:', error);
    } finally {
      setLoadingIdentities(false);
    }
  };

  const loadWebhookSettings = async () => {
    if (!merchant) return;
    try {
      const { data, error } = await epicengageSupabase
        .from('merchant_settings')
        .select('webhook_url, webhook_enabled, webhook_secret')
        .eq('merchant_id', merchant.id)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setWebhookUrl(data.webhook_url || '');
        setWebhookEnabled(data.webhook_enabled || false);
        setWebhookSecret(data.webhook_secret || '');
      }
    } catch (error: any) {
      console.error('Failed to load webhook settings:', error);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Invalid email address');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await epicengageSupabase.functions.invoke('email-identity-add', {
        body: { email: newEmail }
      });
      if (error) throw error;

      toast.success('Verification email sent', {
        description: `Please check ${newEmail} and click the verification link.`
      });
      setAddEmailOpen(false);
      setNewEmail('');
      loadIdentities();
    } catch (error: any) {
      toast.error('Failed to add email', { description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDomain = async () => {
    const cleanedDomain = newDomain
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
    
    if (!cleanedDomain || !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/.test(cleanedDomain)) {
      toast.error('Invalid domain format', { description: 'Please enter a valid domain like "example.com"' });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await epicengageSupabase.functions.invoke('email-identity-domain-add', {
        body: { domain: cleanedDomain }
      });
      if (error) throw error;

      toast.success('Domain added', { description: 'Please configure the DNS records shown.' });
      setAddDomainOpen(false);
      setNewDomain('');
      loadIdentities();
    } catch (error: any) {
      toast.error('Failed to add domain', { description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIdentity = async (id: string) => {
    try {
      const { error } = await epicengageSupabase.functions.invoke('email-identity-delete', {
        body: { id }
      });
      if (error) throw error;
      toast.success('Identity deleted');
      loadIdentities();
    } catch (error: any) {
      toast.error('Failed to delete', { description: error.message });
    }
  };

  const handleRefreshStatuses = async () => {
    if (refreshingStatuses) return;
    setRefreshingStatuses(true);
    try {
      await epicengageSupabase.functions.invoke('email-identity-poll');
      await loadIdentities();
      toast.success('Statuses refreshed');
    } catch (error: any) {
      toast.error('Failed to refresh', { description: error.message });
    } finally {
      setRefreshingStatuses(false);
    }
  };

  const handleResendVerification = async (id: string) => {
    try {
      const { error } = await epicengageSupabase.functions.invoke('email-identity-resend', { body: { id } });
      if (error) throw error;
      toast.success('Verification email resent');
    } catch (error: any) {
      toast.error('Failed to resend', { description: error.message });
    }
  };

  const generateWebhookSecret = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const secret = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    setWebhookSecret(secret);
  };

  const handleSaveWebhookSettings = async () => {
    if (!merchant) return;
    
    // Validate URL if provided
    if (webhookUrl && !webhookUrl.startsWith('https://')) {
      toast.error('Webhook URL must use HTTPS');
      return;
    }

    setSavingWebhook(true);
    try {
      const { error } = await epicengageSupabase
        .from('merchant_settings')
        .update({
          webhook_url: webhookUrl || null,
          webhook_enabled: webhookEnabled,
          webhook_secret: webhookSecret || null
        })
        .eq('merchant_id', merchant.id);

        const postObject = {
          companysupabaseEngageWebhookURL:`${process.env.REACT_APP_EMAIL_SUPABASE_URL}/functions/v1/customer-sync-inbound/${merchant.id}`,
          companysupabaseEngageWebhookSecret:webhookSecret
        }
        axios
          .put(`${apiUrl}company/updateSetting`, postObject, clientheaderconfig)
          .then((res) => {
            if (res.data.status === "ok") {
            }
            console.log(res.data, "res.datares.datares.data");
          })
          .catch((e) => {
          });

        

      if (error) throw error;
      toast.success('Webhook settings saved');
    } catch (error: any) {
      toast.error('Failed to save webhook settings: ' + error.message);
    } finally {
      setSavingWebhook(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-500/10 text-green-600 border-green-200"><CheckCircle2 className="mr-1 h-3 w-3" />Verified</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const emailIdentities = identities.filter(i => i.type === 'email');
  const domainIdentities = identities.filter(i => i.type === 'domain');
  const verifiedSenders = identities.filter(i => i.status === 'verified');

  const handleSaveBusinessProfile = async () => {
    setSaving(true);
    try {
      await updateMerchant({
        name: businessName || 'Your Business',
        industry,
        region
      });
      toast.success('Business profile saved');
    } catch (error: any) {
      toast.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('Settings saved');
    } catch (error: any) {
      toast.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <V1Layout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure your business profile and email sending preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Business Profile */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Business Profile
              </CardTitle>
              <CardDescription>
                Your business information used across the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Your Business"
                />
              </div>
              
              <div>
                <Label>Industry</Label>
                <Select value={industry} onValueChange={(v) => setIndustry(v as IndustryType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Region</Label>
                <Select value={region} onValueChange={(v) => setRegion(v as RegionType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((reg) => (
                      <SelectItem key={reg.value} value={reg.value}>
                        {reg.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleSaveBusinessProfile} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                Save Business Profile
              </Button>
            </CardContent>
          </Card>

          {/* Email Identity Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Sender Identities
              </CardTitle>
              <CardDescription>
                Verify email addresses and domains to send campaigns from
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs defaultValue="emails" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="emails">Email Addresses</TabsTrigger>
                  <TabsTrigger value="domains">Domains</TabsTrigger>
                </TabsList>

                <TabsContent value="emails" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">
                      Verify individual email addresses for sending
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleRefreshStatuses} disabled={refreshingStatuses}>
                        <RefreshCw className={`h-4 w-4 mr-1 ${refreshingStatuses ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                      <Dialog open={addEmailOpen} onOpenChange={setAddEmailOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Plus className="h-4 w-4 mr-1" />
                            Add Email
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Email Address</DialogTitle>
                            <DialogDescription>
                              Enter the email address you want to verify. You'll receive a verification email from AWS.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <Label htmlFor="new-email">Email Address</Label>
                            <Input
                              id="new-email"
                              type="email"
                              placeholder="sender@yourdomain.com"
                              value={newEmail}
                              onChange={(e) => setNewEmail(e.target.value)}
                            />
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAddEmailOpen(false)}>Cancel</Button>
                            <Button onClick={handleAddEmail} disabled={submitting}>
                              {submitting ? 'Adding...' : 'Add Email'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {loadingIdentities ? (
                    <div className="text-center py-4 text-muted-foreground">Loading...</div>
                  ) : emailIdentities.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No email addresses added yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Added</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailIdentities.map((identity) => (
                          <TableRow key={identity.id}>
                            <TableCell className="font-medium">{identity.value}</TableCell>
                            <TableCell>{getStatusBadge(identity.status)}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {format(new Date(identity.requested_at), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {identity.status === 'pending' && (
                                  <Button variant="ghost" size="sm" onClick={() => handleResendVerification(identity.id)}>
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                )}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Email Identity</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete {identity.value}? This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteIdentity(identity.id)}>Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>

                <TabsContent value="domains" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">
                      Verify a domain to send from any address @yourdomain.com
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleRefreshStatuses} disabled={refreshingStatuses}>
                        <RefreshCw className={`h-4 w-4 mr-1 ${refreshingStatuses ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                      <Dialog open={addDomainOpen} onOpenChange={setAddDomainOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Plus className="h-4 w-4 mr-1" />
                            Add Domain
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Domain</DialogTitle>
                            <DialogDescription>
                              Enter your domain to verify it. You'll need to add DNS records to complete verification.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <Label htmlFor="new-domain">Domain</Label>
                            <Input
                              id="new-domain"
                              placeholder="yourdomain.com"
                              value={newDomain}
                              onChange={(e) => setNewDomain(e.target.value)}
                            />
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAddDomainOpen(false)}>Cancel</Button>
                            <Button onClick={handleAddDomain} disabled={submitting}>
                              {submitting ? 'Adding...' : 'Add Domain'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {loadingIdentities ? (
                    <div className="text-center py-4 text-muted-foreground">Loading...</div>
                  ) : domainIdentities.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No domains added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {domainIdentities.map((domain) => (
                        <Card key={domain.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Globe className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{domain.value}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Added {format(new Date(domain.requested_at), 'MMM d, yyyy')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(domain.status)}
                                {domain.status === 'pending' && domain.dns_records && (
                                  <Button variant="outline" size="sm" onClick={() => setSelectedDomain(domain)}>
                                    View DNS
                                  </Button>
                                )}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Domain</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete {domain.value}? This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteIdentity(domain.id)}>Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* DNS Records Modal */}
          <Dialog open={!!selectedDomain} onOpenChange={() => setSelectedDomain(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>DNS Records for {selectedDomain?.value}</DialogTitle>
                <DialogDescription>
                  Add these DNS records to your domain registrar to complete verification.
                </DialogDescription>
              </DialogHeader>
              {selectedDomain?.dns_records && (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {Array.isArray(selectedDomain.dns_records) ? selectedDomain.dns_records.map((record: any, idx: number) => (
                    <Card key={idx}>
                      <CardContent className="p-4 space-y-2">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Type:</span>
                            <p className="font-mono">{record.Type || record.type}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Name:</span>
                            <p className="font-mono text-xs break-all">{record.Name || record.name}</p>
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground text-sm">Value:</span>
                          <p className="font-mono text-xs break-all bg-muted p-2 rounded mt-1">
                            {record.Value || record.value}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <p className="text-muted-foreground">No DNS records available</p>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedDomain(null)}>Close</Button>
                <Button onClick={handleRefreshStatuses} disabled={refreshingStatuses}>
                  {refreshingStatuses ? 'Checking...' : 'Check Verification'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Default Sender Email Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Default Sender
              </CardTitle>
              <CardDescription>
                Choose which verified email to send campaigns from
              </CardDescription>
            </CardHeader>
            <CardContent>
              {verifiedSenders.length === 0 ? (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    No verified sender emails yet. Add and verify an email address above.
                  </p>
                </div>
              ) : (
                <Select
                  value={formData.default_sender_email}
                  onValueChange={(v) => setFormData(f => ({ ...f, default_sender_email: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a verified sender" />
                  </SelectTrigger>
                  <SelectContent>
                    {verifiedSenders.map((sender) => (
                      <SelectItem key={sender.id} value={sender.value}>
                        {sender.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          {/* Tone */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Brand Tone
              </CardTitle>
              <CardDescription>
                The default tone for AI-generated email copy
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.tone}
                onValueChange={(v) => setFormData(f => ({ ...f, tone: v as ToneType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FRIENDLY">Friendly & Casual</SelectItem>
                  <SelectItem value="PROFESSIONAL">Professional</SelectItem>
                  <SelectItem value="PREMIUM">Premium & Luxurious</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Guardrails */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Sending Guardrails
              </CardTitle>
              <CardDescription>
                Protect your customers from email fatigue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">
                  Weekly Email Cap per Customer: {formData.weekly_send_cap}
                </Label>
                <Slider
                  value={[formData.weekly_send_cap]}
                  onValueChange={([v]) => setFormData(f => ({ ...f, weekly_send_cap: v }))}
                  min={1}
                  max={7}
                  step={1}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum emails each customer can receive per week
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quiet Hours Start</Label>
                  <Select
                    value={formData.quiet_hours_start.toString()}
                    onValueChange={(v) => setFormData(f => ({ ...f, quiet_hours_start: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {i.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quiet Hours End</Label>
                  <Select
                    value={formData.quiet_hours_end.toString()}
                    onValueChange={(v) => setFormData(f => ({ ...f, quiet_hours_end: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {i.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Emails won't be sent during quiet hours (currently {formData.quiet_hours_start}:00 - {formData.quiet_hours_end}:00)
              </p>
            </CardContent>
          </Card>

          {/* Discount Ceiling */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Discount Ceiling
              </CardTitle>
              <CardDescription>
                Maximum discount percentage for AI-generated offers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label className="mb-2 block">
                  Maximum Discount: {formData.discount_ceiling_percent}%
                </Label>
                <Slider
                  value={[formData.discount_ceiling_percent]}
                  onValueChange={([v]) => setFormData(f => ({ ...f, discount_ceiling_percent: v }))}
                  min={5}
                  max={50}
                  step={5}
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSaveSettings} disabled={saving} className="w-full">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Email Settings'}
          </Button>

          {/* Customer Sync Webhook - Inbound */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Customer Sync
              </CardTitle>
              <CardDescription>
                Receive customers from your other Epicware products (EpicReview, EpicCommerce)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Webhook URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    readOnly
                    value={merchant ? `${process.env.REACT_APP_EMAIL_SUPABASE_URL}/functions/v1/customer-sync-inbound/${merchant.id}` : 'Loading...'}
                    className="font-mono text-sm bg-muted"
                  />
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => merchant && copyToClipboard(`${process.env.REACT_APP_EMAIL_SUPABASE_URL}/functions/v1/customer-sync-inbound/${merchant.id}`)}
                    disabled={!merchant}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Send POST requests to this URL to sync customers into EpicEngage
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Webhook Secret</Label>
                  <Button variant="ghost" size="sm" onClick={async () => {
                    generateWebhookSecret();
                  }}>
                    Regenerate
                  </Button>
                </div>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      readOnly
                      type={showSecret ? 'text' : 'password'}
                      value={webhookSecret || 'No secret configured'}
                      className="font-mono text-sm bg-muted pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {webhookSecret && (
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(webhookSecret)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Use this secret to sign requests with X-EpicEngage-Signature header (HMAC-SHA256)
                </p>
              </div>

              {webhookSecret !== (settings as any)?.webhook_secret && (
                <Button onClick={handleSaveWebhookSettings} disabled={savingWebhook} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  {savingWebhook ? 'Saving...' : 'Save Secret'}
                </Button>
              )}

              <div className="bg-muted p-3 rounded-lg text-sm">
                <p className="font-medium mb-2">Expected Payload Format:</p>
                <pre className="text-xs overflow-x-auto bg-background p-2 rounded">
{`{
  "source": "EpicReview | EpicCommerce | Other",
  "customers": [
    {
      "external_customer_id": "<string>",
      "name": "<string>",
      "email": "<string>",
      "status": "active | inactive",
      "joined_at": "<ISO timestamp>",
      "last_activity_at": "<ISO timestamp>"
    }
  ]
}`}
                </pre>
              </div>

              <div className="bg-muted/50 border border-border p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">Response</p>
                <p className="text-muted-foreground text-xs">
                  On success: <code className="bg-background px-1 rounded">{"{ \"received\": <count> }"}</code>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </V1Layout>
  );
}